import { Suspense, useEffect, type ComponentType, type ReactNode } from 'react';
import { useDeferredInView } from '@/lib/useDeferredInView';
import { isPrerenderMode } from '@/lib/isPrerenderMode';

/**
 * Dodatni rootMargin — obserwator zgłasza "in view" 160px PRZED faktycznym
 * wejściem sekcji w viewport, więc jej chunk zdąży się pobrać zanim
 * użytkownik tam doscrolluje (przy normalnym tempie scrolla nie widać
 * pustego placeholdera). To jedyny strojony parametr tego mechanizmu.
 */
const PRELOAD_MARGIN = '160px 0px';

/**
 * Sygnalizuje onMounted() dopiero gdy React faktycznie scommitował dzieci —
 * czyli lazy chunk sekcji się rozwiązał i wyrenderował, nie tylko "zaczął się
 * ładować". Używane przez Home, żeby usePrerenderReady() czekał na komplet
 * sekcji podczas prerenderingu (patrz pages/home.tsx).
 *
 * Empty deps celowo — to sygnał "zamontowano raz", nie reakcja na zmianę
 * tożsamości callbacka (Home przekazuje nowe domknięcie przy każdym renderze
 * licznika zamontowanych sekcji; reagowanie na to odpaliłoby efekt ponownie
 * i zapętliło inkrementację).
 */
function MountSignal({ onMounted, children }: { onMounted?: () => void; children: ReactNode }) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { onMounted?.(); }, []);
  return <>{children}</>;
}

/**
 * Opakowuje sekcję z SECTION_REGISTRY viewport-gated deferred loadingiem.
 * Poza Hero (który jest renderowany od razu jako element LCP)
 * oraz kafelkami kategorii pozostałe sekcje czekają na zbliżenie viewportu.
 * Wspólny IntersectionObserver otrzymuje pomiary od przeglądarki bez
 * wymuszania synchronicznego przeliczania układu podczas hydratacji.
 * Stała wysokość placeholdera zachowuje miejsce do czasu pobrania chunka.
 *
 * W trybie prerenderingu (Playwright, patrz isPrerenderMode) omija
 * IntersectionObserver całkowicie — Playwright nie przewija strony, więc
 * bez tego snapshot HTML nigdy nie zawierałby treści poniżej pierwszego
 * ekranu (SEO tekst, opinie, polecane produkty).
 */
export function DeferredSection({
  Component,
  config,
  placeholderHeight,
  onMounted,
}: {
  Component: ComponentType<any>;
  config?: unknown;
  /**
   * Wysokość placeholdera per breakpoint (patrz SectionPlaceholderHeight w
   * lib/sections/types.ts) — zmierzona per sekcja, nie jeden uniwersalny
   * rozmiar. Przekazana jako CSS custom properties + klasa ze scoped media
   * query w index.css (`.bk-deferred-placeholder`), a nie JS-owa detekcja
   * viewportu — przeglądarka dobiera właściwą wartość PRZED pierwszym
   * malowaniem, zero ryzyka mignięcia złej wysokości.
   */
  placeholderHeight: { mobile: number; desktop: number };
  onMounted?: () => void;
}) {
  const { ref, inView } = useDeferredInView<HTMLDivElement>(PRELOAD_MARGIN);
  const placeholderStyle = {
    '--bk-ph-mobile': `${placeholderHeight.mobile}px`,
    '--bk-ph-desktop': `${placeholderHeight.desktop}px`,
  } as React.CSSProperties;

  if (isPrerenderMode || inView) {
    return (
      <Suspense fallback={<div className="bk-deferred-placeholder" style={placeholderStyle} />}>
        <MountSignal onMounted={onMounted}>
          <Component {...(config ? { config } : {})} />
        </MountSignal>
      </Suspense>
    );
  }

  return <div ref={ref} className="bk-deferred-placeholder" style={placeholderStyle} aria-hidden="true" />;
}
